import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import StatusBadge from "@/components/StatusBadge";
import { addProviderTileLayer } from "@/lib/mapProvider";
import { Truck, Phone, MapPin, Radio, Package, Clock, User, ExternalLink, RefreshCcw } from "lucide-react";

// Fix Leaflet default marker paths (assets are pulled from a CDN so the bundler
// doesn't need to copy them into /public — same pattern used across the app).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Truck marker for a bowser mid-trip. Colour reflects freshness of the last ping.
function bowserIcon(isLive) {
  const color = isLive ? "#2563eb" : "#9ca3af";
  return L.divIcon({
    className: "trip-bowser-marker",
    html: `<div style="background:${color};border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.4);width:32px;height:32px;border-radius:16px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;">T</div>`,
    iconSize: [32, 32], iconAnchor: [16, 16],
  });
}

// Flag marker for the delivery destination.
function siteIcon() {
  return L.divIcon({
    className: "trip-site-marker",
    html: `<div style="background:#dc2626;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.4);width:28px;height:28px;border-radius:14px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:11px;">D</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
  });
}

// In-memory route cache keyed by rounded coord pair — avoids re-fetching the
// same driver→customer polyline within a single admin session.
const _routeCache = new Map();
async function fetchRoute(fromLat, fromLng, toLat, toLng) {
  const key = `${fromLat.toFixed(4)},${fromLng.toFixed(4)}|${toLat.toFixed(4)},${toLng.toFixed(4)}`;
  if (_routeCache.has(key)) return _routeCache.get(key);
  const promise = api
    .get(`/routing/directions?from_lat=${fromLat}&from_lng=${fromLng}&to_lat=${toLat}&to_lng=${toLng}`)
    .then((r) => r.data);
  _routeCache.set(key, promise);
  try {
    return await promise;
  } catch (e) {
    _routeCache.delete(key); // don't cache failures
    throw e;
  }
}

// Straight-line distance in metres (haversine) — used to decide when to
// re-fetch the route (bowser moved ≥ 500 m).
function distMeters(a, b) {
  const R = 6371e3;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * TripsMap — imperative Leaflet wrapper that draws every ongoing trip:
 *   * Bowser position (blue T pin)
 *   * Destination site (red D pin, if coords available)
 *   * Dashed line between them (planned route stub)
 *
 * The map auto-fits its bounds to whatever markers exist, and pans/zooms to a
 * selected trip when the user clicks a card on the left.
 */
function TripsMap({ trips, selectedId }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({}); // trip_id → { bowser, site, line }

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) return;
    const map = L.map(containerRef.current, {
      center: [20.5937, 78.9629], // India centroid — fits whatever comes in
      zoom: 5,
      zoomControl: true,
    });
    addProviderTileLayer(L, map);
    mapRef.current = map;
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layersRef.current = {};
      }
    };
  }, []);

  // Sync markers whenever trips list changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    const boundsPts = [];

    trips.forEach((t) => {
      seen.add(t.order_id);
      const existing = layersRef.current[t.order_id] || {};
      const hasBowser = t.position && typeof t.position.lat === "number";
      const hasSite = typeof t.site_lat === "number" && typeof t.site_lng === "number" && (t.site_lat || t.site_lng);

      // Bowser marker
      if (hasBowser) {
        const latlng = [t.position.lat, t.position.lng];
        boundsPts.push(latlng);
        if (existing.bowser) {
          existing.bowser.setLatLng(latlng);
          existing.bowser.setIcon(bowserIcon(t.position.is_live));
        } else {
          existing.bowser = L.marker(latlng, { icon: bowserIcon(t.position.is_live) })
            .bindPopup(popupHtml(t))
            .bindTooltip(`${t.order_code} · ${t.bowser?.code || "—"}`)
            .addTo(map);
        }
      } else if (existing.bowser) {
        map.removeLayer(existing.bowser);
        existing.bowser = null;
      }

      // Destination marker
      if (hasSite) {
        const dst = [t.site_lat, t.site_lng];
        boundsPts.push(dst);
        if (existing.site) {
          existing.site.setLatLng(dst);
        } else {
          existing.site = L.marker(dst, { icon: siteIcon() })
            .bindPopup(`<div style="font-size:12px;"><b>${escapeHtml(t.site_name || "Destination")}</b><br/>${escapeHtml(t.site_address || "")}</div>`)
            .bindTooltip(`Drop-off · ${escapeHtml(t.customer_name || "")}`)
            .addTo(map);
        }
      } else if (existing.site) {
        map.removeLayer(existing.site);
        existing.site = null;
      }

      // Real driving-route polyline from bowser to destination via OSRM.
      // We fetch it once per trip when both endpoints are known and cache the
      // route on the layers object so we only re-query when the destination
      // changes or the bowser drifts significantly (>500m). While waiting for
      // the fetch we show a subtle dashed line so the user isn't looking at
      // an empty map.
      if (hasBowser && hasSite) {
        const straight = [[t.position.lat, t.position.lng], [t.site_lat, t.site_lng]];
        if (!existing.line) {
          existing.line = L.polyline(straight, {
            color: "#f97316", weight: 2, dashArray: "6, 6", opacity: 0.6,
          }).addTo(map);
        }
        const bowserMoved = existing.lastFrom
          ? distMeters(existing.lastFrom, [t.position.lat, t.position.lng]) > 500
          : true;
        const destChanged = existing.lastTo
          ? existing.lastTo[0] !== t.site_lat || existing.lastTo[1] !== t.site_lng
          : true;
        if ((bowserMoved || destChanged) && !existing.routeFetching) {
          existing.routeFetching = true;
          fetchRoute(t.position.lat, t.position.lng, t.site_lat, t.site_lng)
            .then((data) => {
              if (!mapRef.current || !layersRef.current[t.order_id]) return;
              const l = layersRef.current[t.order_id];
              if (data.coords && data.coords.length > 1 && l.line) {
                l.line.setLatLngs(data.coords);
                l.line.setStyle({
                  color: data.provider === "osrm" ? "#2563eb" : "#f97316",
                  weight: 4,
                  opacity: 0.85,
                  dashArray: data.provider === "osrm" ? null : "6, 6",
                });
                if (l.bowser) {
                  const km = data.distance_km?.toFixed(1);
                  const min = Math.round(data.duration_min || 0);
                  l.bowser.bindTooltip(`${t.order_code} · ${km} km · ${min} min ETA`, { permanent: false });
                }
              }
              l.lastFrom = [t.position.lat, t.position.lng];
              l.lastTo = [t.site_lat, t.site_lng];
              l.routeMeta = { distance_km: data.distance_km, duration_min: data.duration_min, provider: data.provider };
            })
            .catch(() => {
              // Silently fall back to the straight dashed line already drawn
            })
            .finally(() => {
              if (layersRef.current[t.order_id]) {
                layersRef.current[t.order_id].routeFetching = false;
              }
            });
        }
      } else if (existing.line) {
        map.removeLayer(existing.line);
        existing.line = null;
        existing.lastFrom = null;
        existing.lastTo = null;
        existing.routeMeta = null;
      }

      layersRef.current[t.order_id] = existing;
    });

    // Remove stale
    Object.keys(layersRef.current).forEach((id) => {
      if (!seen.has(id)) {
        const l = layersRef.current[id];
        if (l.bowser) map.removeLayer(l.bowser);
        if (l.site) map.removeLayer(l.site);
        if (l.line) map.removeLayer(l.line);
        delete layersRef.current[id];
      }
    });

    // Fit bounds on first draw / whenever the trip set changes.
    if (boundsPts.length > 0 && !selectedId) {
      const bounds = L.latLngBounds(boundsPts);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [trips, selectedId]);

  // Zoom / pan to the selected trip
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const t = trips.find((x) => x.order_id === selectedId);
    if (!t) return;
    const pts = [];
    if (t.position && typeof t.position.lat === "number") pts.push([t.position.lat, t.position.lng]);
    if (typeof t.site_lat === "number" && (t.site_lat || t.site_lng)) pts.push([t.site_lat, t.site_lng]);
    if (pts.length === 1) map.setView(pts[0], 13);
    else if (pts.length === 2) map.fitBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 13 });
    // Also open the bowser popup for the selected trip
    const layer = layersRef.current[selectedId];
    if (layer?.bowser) layer.bowser.openPopup();
  }, [selectedId, trips]);

  return <div ref={containerRef} style={{ height: "620px", width: "100%", borderRadius: "0.5rem" }} data-testid="ongoing-trips-map" />;
}

function popupHtml(t) {
  const stock = t.bowser?.current_stock ? `${Math.round(t.bowser.current_stock)}L` : "—";
  const capacity = t.bowser?.capacity ? ` / ${t.bowser.capacity}L` : "";
  return `<div style="font-size:12px;min-width:180px;">
    <div style="font-weight:700;font-size:13px;">${escapeHtml(t.order_code)} · ${escapeHtml(t.status)}</div>
    <div style="margin-top:2px;">${escapeHtml(t.customer_name || "")}</div>
    <div style="color:#64748b;">${escapeHtml(t.site_name || t.site_address || "")}</div>
    <hr style="margin:6px 0;border:none;border-top:1px solid #e5e7eb;" />
    <div><b>Bowser:</b> ${escapeHtml(t.bowser?.code || "—")} · ${escapeHtml(t.bowser?.registration || "")}</div>
    <div><b>Stock:</b> ${stock}${capacity}</div>
    <div><b>Driver:</b> ${escapeHtml(t.driver?.name || "—")} ${t.driver?.phone ? "· " + escapeHtml(t.driver.phone) : ""}</div>
  </div>`;
}

// Minimal HTML escape for popup interpolation — DB values could contain unsafe chars.
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;",
  }[c]));
}

// Human-friendly relative time — "just now", "12m ago", "3h ago", etc.
function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!then) return "—";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function OngoingTrips() {
  const [selectedId, setSelectedId] = useState(null);
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["dispatch-ongoing-trips"],
    queryFn: () => api.get("/dispatch/ongoing-trips").then((r) => r.data),
    refetchInterval: 8000,
  });
  const trips = useMemo(() => data?.items || [], [data]);
  const staleAfter = data?.stale_after_min || 15;

  // If the previously-selected trip drops out of the list (delivered / cancelled),
  // clear the selection so the map returns to auto-fit.
  useEffect(() => {
    if (selectedId && !trips.find((t) => t.order_id === selectedId)) {
      setSelectedId(null);
    }
  }, [trips, selectedId]);

  const liveCount = useMemo(() => trips.filter((t) => t.position?.is_live).length, [trips]);

  return (
    <div className="space-y-4" data-testid="ongoing-trips-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Ongoing Trips</h1>
          <p className="text-sm text-muted-foreground">
            {trips.length} active {trips.length === 1 ? "trip" : "trips"} · {liveCount} live · positions stale after {staleAfter} min · auto-refresh every 8s
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="btn-refresh-trips"
        >
          <RefreshCcw size={14} className={`mr-2 ${isFetching ? "animate-spin" : ""}`} />
          {isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Trips list — left column on desktop, on top on mobile */}
        <Card className="lg:col-span-2">
          <CardHeader className="border-b border-border/60 py-3">
            <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
              Trip Roster
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[620px] space-y-2 overflow-y-auto p-3">
            {trips.length === 0 && (
              <div className="py-10 text-center" data-testid="no-ongoing-trips">
                <Truck size={28} className="mx-auto text-muted-foreground" />
                <div className="mt-2 text-sm font-semibold">No ongoing trips right now</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Trips will appear here the moment an order is dispatched.
                </div>
                <Link to="/app/dispatch" className="mt-3 inline-block text-xs font-semibold text-primary hover:underline">
                  → Go to Dispatch Board
                </Link>
              </div>
            )}

            {trips.map((t) => {
              const isSel = t.order_id === selectedId;
              return (
                <button
                  key={t.order_id}
                  type="button"
                  onClick={() => setSelectedId(t.order_id)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    isSel
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-border/60 hover:border-primary/50 hover:bg-muted/40"
                  }`}
                  data-testid={`trip-card-${t.order_code}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold">{t.order_code}</span>
                      <StatusBadge status={t.status} />
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${t.position?.is_live ? "border-green-500 text-green-700" : "border-slate-400 text-slate-500"}`}
                    >
                      <Radio size={9} className="mr-1" />
                      {t.position?.is_live ? "Live" : t.position ? "Stale" : "No fix"}
                    </Badge>
                  </div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{t.customer_name || "—"}</div>
                  <div className="flex items-start gap-1 text-xs text-muted-foreground">
                    <MapPin size={11} className="mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{t.site_name || t.site_address || "No delivery address"}</span>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Truck size={11} />
                      <span className="font-mono">{t.bowser?.code || "—"}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Package size={11} />
                      <span className="font-mono">{Math.round(t.quantity || 0)}L</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <User size={11} />
                      <span className="line-clamp-1">{t.driver?.name || "—"}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock size={11} />
                      <span>{relTime(t.dispatched_at || t.created_at)}</span>
                    </div>
                  </div>

                  {t.driver?.phone && (
                    <a
                      href={`tel:${t.driver.phone}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`trip-call-driver-${t.order_code}`}
                    >
                      <Phone size={11} /> {t.driver.phone}
                    </a>
                  )}
                  <Link
                    to={`/app/orders/${t.order_id}`}
                    className="ml-3 mt-2 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`trip-open-order-${t.order_code}`}
                  >
                    <ExternalLink size={11} /> Order
                  </Link>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Map — right side */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            <TripsMap trips={trips} selectedId={selectedId} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
